import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { useUser } from '@myosh/myosh-login';
import { Loader } from '@myosh/odin-components';
import MobileWebViewRecord from './mobile-webview-record.component';
import { useTranslation } from 'react-i18next';

/**
 * This page is exclusively for rendering records in the React Native WebView.
 * It skips the ClusterProvider, UserLocaleInitializer, and ThemeProvider since:
 * - Loading these would show unnecessary "loading configuration/theme" screens each time it's opened
 * - All required data is injected by the mobile app
 */
export default function MobileWebViewRecordPage() {
  const { formId: formIdParam, recordId: recordIdParam } = useParams();
  const { state } = useUser();
  const { auth: isAuthenticated, loginStatus } = state;
  const [authTimeout, setAuthTimeout] = useState(false);
  const { t } = useTranslation();

  const formId = formIdParam ? parseInt(formIdParam, 10) : undefined;
  const recordId = recordIdParam ? parseInt(recordIdParam, 10) : undefined;

  // If auth doesn't complete within 10 seconds, something went wrong with injection
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (!isAuthenticated || loginStatus !== 'done') {
        setAuthTimeout(true);
        // Log error so WebView console capture can report it to mobile app
        // eslint-disable-next-line no-console
        console.error('WebView auth injection timeout: Authentication failed to complete within 10 seconds', {
          isAuthenticated,
          loginStatus,
          formId,
          recordId,
        });
      }
    }, 10000);

    return () => clearTimeout(timeout);
  }, [isAuthenticated, loginStatus, formId, recordId]);

  if (isAuthenticated && loginStatus === 'done' && formId) {
    return <MobileWebViewRecord formId={formId} recordId={recordId} />;
  }

  if (authTimeout) {
    return (
      <div className="mx-auto flex h-screen w-full flex-col items-center justify-center gap-4 px-4 text-center">
        <p className="text-error">{t('error-webview-auth-timeout')}</p>
      </div>
    );
  }

  // Show loader while waiting for auth injection from mobile app
  return (
    <div className="mx-auto flex h-screen w-full flex-col items-center justify-center">
      <Loader />
    </div>
  );
}
